/**
 * Cards for the inter-agent tools.
 *
 * `DelegationStep`: `delegate_to_assistant` ran heavy work on one client or
 * case inside this turn; one nested step naming whom it worked on, with the
 * run's steps, its reply and what it changed inside.
 *
 * `HandoffCard` and `AskAgentCard` render the pre-merge `handoff_to_agent` and
 * `ask_agent` results so old transcripts still read.
 */
import type { DynamicToolUIPart, ToolUIPart } from "ai";
import { ArrowRight, ArrowRightLeft, ExternalLink, Users } from "lucide-react";
import { useState, type FC } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AgentAPI } from "@/features/agents/api/agentApi";
import { elsewherePath } from "@/features/agents/hooks/useElsewhereThreads";
import { ChatProse } from "@/features/agents/refs";
import {
  agentLabel as agentLabelOf,
  caseyName,
  type AskAgentResult,
  type DelegationResult,
  type HandoffResult
} from "@/features/agents/types";
import type { StepData } from "@/features/agents/useAgentChat";
import { AgentMoodAvatar } from "@/features/agents/components/AgentMoodAvatar";
import { useClientNames } from "@/features/dashboard/useClientNames";
import { cn } from "@/lib/utils";

import type { AnyToolPart } from "./ToolCard";
import { ActivityLine, rowStatusOf, StepRows } from "./ToolRow";

/** The answering assistant's face. */
const ReplyFace: FC<{ scopeKind: string; scopeId: string }> = ({
  scopeKind,
  scopeId
}) => (
  <AgentMoodAvatar
    animated
    size="xs"
    className="mt-0.5 rounded-xs"
    agent={{ key: `${scopeKind}:${scopeId}` }}
  />
);

const SCOPE_NOUN: Record<HandoffResult["scope_kind"], string> = {
  case: "Case",
  client: "Client",
  practice: "Practice"
};

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null;

export const HandoffCard: FC<{
  handoff: HandoffResult;
  className?: string;
}> = ({ handoff, className }) => {
  const navigate = useNavigate();
  const agentLabel = caseyName(
    handoff.agent_label || agentLabelOf(handoff.agent)
  );
  const scopeLabel =
    handoff.scope_name ||
    `${SCOPE_NOUN[handoff.scope_kind]} ${handoff.scope_id}`;
  const title = handoff.thread_title ?? handoff.title ?? null;
  return (
    <div
      data-testid="handoff-card"
      className={cn(
        "space-y-1.5 rounded-md border border-line-2 bg-fill-1 px-3 py-2 text-sm",
        className
      )}
    >
      <div className="flex flex-wrap items-center gap-1.5">
        <ArrowRightLeft className="size-3.5 text-primary" />
        <span className="font-medium">Handed to {agentLabel}</span>
        <Badge variant="secondary" className="h-5 gap-1 text-xs">
          {SCOPE_NOUN[handoff.scope_kind]}
          <span className="max-w-40 truncate font-normal">{scopeLabel}</span>
        </Badge>
        {handoff.awaiting_approval ? (
          <Badge
            variant="outline"
            className="h-5 border-warn-line text-xs text-warn"
          >
            Awaiting Approval There
          </Badge>
        ) : null}
      </div>
      {title ? (
        <div className="truncate text-muted-foreground" title={title}>
          {title}
        </div>
      ) : null}
      {handoff.error ? (
        <div className="text-destructive">{handoff.error}</div>
      ) : handoff.reply ? (
        <ChatProse className="text-sm">{handoff.reply}</ChatProse>
      ) : null}
      <Button
        variant="outline"
        size="xs"
        className="h-6 gap-1 text-xs"
        onClick={() => navigate(handoff.link)}
        data-testid="handoff-open"
      >
        Open in {agentLabel} <ExternalLink className="size-3" />
      </Button>
    </div>
  );
};

const askAgentResult = (output: unknown): AskAgentResult | null => {
  if (!isRecord(output) || typeof output.agent !== "string") return null;
  return output as unknown as AskAgentResult;
};

export const AskAgentCard: FC<{ part: ToolUIPart | DynamicToolUIPart }> = ({
  part
}) => {
  const input = isRecord(part.input) ? part.input : {};
  const askedAgent =
    typeof input.agent === "string" ? input.agent : "another agent";
  const result =
    part.state === "output-available" ? askAgentResult(part.output) : null;
  const label = caseyName(result?.agent_label ?? agentLabelOf(askedAgent));
  const error =
    part.state === "output-error"
      ? part.errorText
      : result?.error ??
        (part.state === "output-available" &&
        isRecord(part.output) &&
        typeof part.output.error === "string"
          ? part.output.error
          : undefined);
  const question = typeof input.question === "string" ? input.question : null;
  const done = Boolean(result) || Boolean(error);
  const verb = done ? "Asked" : "Asking";
  return (
    <ActivityLine
      icon={Users}
      status={error ? "error" : done ? "done" : "running"}
      title={`${verb} ${label}${question ? `: ${question}` : ""}`}
      defaultOpen={Boolean(result)}
      testId="ask-agent-card"
      data={{ tool: "ask_agent" }}
    >
      <div className="space-y-1.5 text-sm">
        {question ? (
          <div className="text-muted-foreground">
            <span className="font-medium text-fg-2">Question:</span> {question}
          </div>
        ) : null}
        {error ? (
          <div className="text-destructive">{error}</div>
        ) : result ? (
          <div className="flex gap-2">
            <ReplyFace
              scopeKind={result.scope_kind}
              scopeId={result.scope_id}
            />
            <ChatProse className="min-w-0 flex-1 text-sm">
              {result.answer}
            </ChatProse>
          </div>
        ) : null}
      </div>
    </ActivityLine>
  );
};

/** `delegate_to_assistant` output → the result, or null while it runs. */
export const delegationResult = (output: unknown): DelegationResult | null => {
  if (!isRecord(output)) return null;
  if (typeof output.error === "string" && !output.status)
    return {
      status: "completed",
      scope_kind:
        (output.scope_kind as DelegationResult["scope_kind"]) ?? "case",
      scope_id: String(output.scope_id ?? ""),
      error: output.error
    };
  if (output.status !== "completed" && output.status !== "running") return null;
  return output as unknown as DelegationResult;
};

const countOf = (v: unknown): number =>
  Array.isArray(v) ? v.length : typeof v === "number" ? v : 0;

/** Continue in Case: a thread at the delegated case or client, seeded with the run. */
const ContinueButton: FC<{
  kind: "case" | "client";
  scopeId: string;
  sourceId: string;
  toolCallId: string;
}> = ({ kind, scopeId, sourceId, toolCallId }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const thread = await AgentAPI.continueThread(
        { kind, id: scopeId },
        sourceId,
        toolCallId
      );
      navigate(elsewherePath(thread));
    } catch (e) {
      toast.error((e as Error).message || "Couldn't continue there");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      variant="secondary"
      size="xs"
      className="self-start"
      disabled={busy}
      onClick={() => void go()}
      data-testid="delegation-continue"
    >
      {kind === "case" ? "Continue in Case" : "Continue in Client"}
      <ArrowRight />
    </Button>
  );
};

/**
 * Inside a delegation step once it has an output: the reply, what changed
 * (undoable from this thread), or that the work continues in the background.
 */
const DelegationResultBody: FC<{
  result: DelegationResult;
  toolCallId: string;
}> = ({ result, toolCallId }) => {
  const background = result.status === "running";
  const changes = countOf(result.change_ids);
  const calls = countOf(result.tools_called);
  return (
    <div
      data-testid="delegation-card"
      data-status={result.status}
      className="flex flex-col gap-1.5 text-sm"
    >
      {result.error ? (
        <div className="text-destructive">{result.error}</div>
      ) : null}
      {result.reply ? (
        <ChatProse className="text-sm">{result.reply}</ChatProse>
      ) : null}
      {background && result.note ? (
        <div className="text-fg-3">{result.note}</div>
      ) : null}
      {!background && (calls || changes || result.tool_errors) ? (
        <div className="flex flex-wrap gap-1.5">
          {calls ? (
            <Badge variant="secondary" className="h-5 text-xs">
              {calls} Step{calls === 1 ? "" : "s"}
            </Badge>
          ) : null}
          {changes ? (
            <Badge variant="secondary" className="h-5 text-xs">
              {changes} Change{changes === 1 ? "" : "s"}, Undoable Here
            </Badge>
          ) : null}
          {result.tool_errors ? (
            <Badge
              variant="outline"
              className="h-5 border-warn-line text-xs text-warn"
            >
              {result.tool_errors} Error{result.tool_errors === 1 ? "" : "s"}
            </Badge>
          ) : null}
        </div>
      ) : null}
      {!background &&
      !result.error &&
      result.thread_id &&
      (result.scope_kind === "case" || result.scope_kind === "client") ? (
        <ContinueButton
          kind={result.scope_kind}
          scopeId={result.scope_id}
          sourceId={result.thread_id}
          toolCallId={toolCallId}
        />
      ) : null}
    </div>
  );
};

/**
 * `delegate_to_assistant` as one nested step, like Cursor's Task row:
 * "Working on Garcia" while the run's own calls stream in under it, then
 * "Worked on Garcia" with the run's reply and what it changed inside.
 */
export const DelegationStep: FC<{
  part: AnyToolPart;
  steps?: readonly StepData[];
}> = ({ part, steps }) => {
  const input = isRecord(part.input) ? part.input : {};
  const kind = input.scope_kind === "case" ? "case" : "client";
  const scopeId = typeof input.scope_id === "string" ? input.scope_id : "";
  const result =
    part.state === "output-available" ? delegationResult(part.output) : null;
  const { nameOf } = useClientNames();
  const name =
    result?.scope_name ||
    (kind === "client" ? nameOf(scopeId) : null) ||
    (kind === "case" ? "a case" : "a client");
  const status = rowStatusOf(part);
  const live = Boolean(steps && steps.length > 0);
  const title =
    result?.status === "running"
      ? `Still working on ${name}`
      : status === "done" || status === "error"
      ? `Worked on ${name}`
      : `Working on ${name}`;
  const errorText = part.state === "output-error" ? part.errorText : null;
  const body =
    live || result || errorText ? (
      <div className="flex flex-col gap-1.5">
        {live && steps ? <StepRows steps={steps} /> : null}
        {result ? (
          <DelegationResultBody result={result} toolCallId={part.toolCallId} />
        ) : null}
        {errorText ? (
          <div className="text-sm text-destructive">{errorText}</div>
        ) : null}
      </div>
    ) : null;
  return (
    <ActivityLine
      icon={Users}
      status={result?.status === "running" ? "done" : status}
      title={title}
      defaultOpen={live}
      testId="tool-row"
      data={{ tool: "delegate_to_assistant", "state-tool": part.state }}
    >
      {body}
    </ActivityLine>
  );
};
